import * as d3 from 'd3';
import { createLegend, createTooltip, ElementWithCleanup, formatValue, getGroupColor, hideTooltip, InnerValue, positionTooltip, roundDigitsAfterDecimal, setupTooltipHiding, shouldShowLegend, showTooltip, trimLabel, ValueNameData } from '../cross_platform/dashboard_script';
import { SurfaceColor } from '../../../styles/global/css/Utils';
import { TitleFontFamily } from '../../../styles/global/css/GlobalText';

const DOUGHNUT_INNER_RATIO = 0.72;
const SIDE_LEGEND_CHART_SHARE = 0.6;

export function createPieSvg({
	chartData,
	width,
	height,
	marginLeftRight,
	marginTopBottom,
	tooltipId,
	opts,
	onValueClick
} : {
	chartData: ValueNameData[],
	width: number,
	height: number,
	marginLeftRight?: number,
	marginTopBottom?: number,
	tooltipId?: string,
	opts?: {
		locale?: string,
		number_extension?: string | number,
		max_values?: number,
		digits_after_decimal?: number,
		legend?: boolean,
		legendItemSpacing?: number,
		max_legend_items?: number,
		legend_orientation?: 'horizontal' | 'vertical',
		hide_value_labels?: boolean,
		doughnut?: boolean,
		trim_x_labels?: number,
		distance_from_center?: number,
		min_slice_angle?: number
	},
	onValueClick?: (value: InnerValue) => void,
}): SVGSVGElement | null {
	const mlr = marginLeftRight ?? 0;
	const mtb = marginTopBottom ?? 0;
	const svgHeight = height + mtb * 2;

	// Create the pie layout and arc generator.
	const pie = d3.pie<ValueNameData>()
		.sort((a, b) => b.value - a.value)
		.value(d => d.value);

	// Respect `opts.max_values`: sort descending and only keep top N values.
	let displayData = chartData;
	if (opts?.max_values && chartData.length > Number(opts.max_values)) {
		displayData = chartData.slice().sort((a, b) => b.value - a.value).slice(0, Number(opts.max_values));
	}
	const sortedKeys = displayData.map(d => d.name).filter(name => name != null && name.length > 0);
	const showLegend = shouldShowLegend(opts, sortedKeys);
	const sideLegend = showLegend && (opts?.legend_orientation ?? 'vertical') === 'vertical';

	// With a side legend the pie takes the left share of the width and the legend the rest
	const chartWidth = sideLegend ? width * SIDE_LEGEND_CHART_SHARE : width;
	const innerWidth = Math.max(0, chartWidth - mlr * 2);
	const innerHeight = Math.max(0, height - mtb * 2);
	const baseOuter = Math.max(0, Math.min(innerWidth, innerHeight) / 2 - 1);
	const baseInner = opts?.doughnut ? baseOuter * DOUGHNUT_INNER_RATIO : 0;
	const arc = d3.arc<d3.PieArcDatum<ValueNameData>>()
		.innerRadius(baseInner)
		.outerRadius(baseOuter);

	const labelRadius = baseOuter * (1 + (opts?.distance_from_center ?? 5) / 10);

	// A separate arc generator for labels.
	const arcLabel = d3.arc<d3.PieArcDatum<ValueNameData>>()
		.innerRadius(labelRadius)
		.outerRadius(labelRadius);

	const arcs = pie(displayData);

	const grandTotal = chartData.reduce((acc, d) => acc + (typeof d.value === 'number' ? d.value : 0), 0);
	const percentFormat = new Intl.NumberFormat(opts?.locale ?? 'fr-FR', { style: 'percent', maximumFractionDigits: 1 });
	const sliceShare = (d: ValueNameData) => percentFormat.format(grandTotal > 0 ? d.value / grandTotal : 0);
	const sliceTooltip = (d: ValueNameData) => `<div style="font-weight:600">${d.name}</div><div style="opacity:0.9">${formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)} <span style="opacity:0.7">(${sliceShare(d)})</span></div>`;

	// Create the SVG container.
	const svg = d3.create('svg')
		.attr('width', width)
		.attr('height', svgHeight)
		.attr('viewBox', [-width / 2 - mlr, -height / 2 - mtb, width + mlr * 2, svgHeight])
		.attr('style', 'max-width: 100%; height: auto;')
		.attr('fill', 'var(--on-surface-color)');

	const chartG = svg.append('g').attr('transform', `translate(${sideLegend ? (chartWidth - width) / 2 : 0},0)`);

	// Add a sector path for each value.
	chartG.append('g')
		.attr('stroke', SurfaceColor)
		.selectAll()
		.data(arcs)
		.join('path')
		.attr('fill', (d: d3.PieArcDatum<ValueNameData>) => getGroupColor(d.data.name) as string)
		.attr('d', (d: d3.PieArcDatum<ValueNameData>) => (arc(d) as string))
		.style('cursor', onValueClick ? 'pointer' : 'default');

	if (opts?.doughnut && baseInner > 0) {
		const totalText = formatValue(roundDigitsAfterDecimal(grandTotal, opts?.digits_after_decimal), opts);
		const fontSize = Math.max(14, Math.min(40, (baseInner * 2.4) / Math.max(totalText.length, 1)));
		chartG.append('text')
			.attr('class', 'pie-center-value')
			.attr('text-anchor', 'middle')
			.attr('dominant-baseline', 'central')
			.attr('font-family', TitleFontFamily)
			.attr('font-weight', '500')
			.attr('fill', 'var(--on-surface-color)')
			.style('font-size', `${fontSize}px`, 'important')
			.style('pointer-events', 'none')
			.text(totalText)
			.append('title')
			.text(totalText);
	}

	// Create reusable tooltip
	const tooltipDiv = createTooltip({ tooltipId });

	// Prepare hover arc (slightly bigger outer radius)
	const hoverOuter = baseOuter + 8;
	const hoverInner = opts?.doughnut ? Math.max(0, baseInner - 4) : 0;
	const arcHover = d3.arc<d3.PieArcDatum<ValueNameData>>()
		.innerRadius(hoverInner)
		.outerRadius(hoverOuter);

	let selectedIndex: number | null = null;

	// Add interaction handlers to the slices
	// Use arrow functions and event.currentTarget to avoid using `this` and improve typing.
	svg.selectAll<SVGPathElement, d3.PieArcDatum<ValueNameData>>('path')
		.on('mouseover', (event: MouseEvent, d: d3.PieArcDatum<ValueNameData>) => {
			const node = event.currentTarget as SVGPathElement;
			// enlarge hovered slice
			d3.select(node).transition().duration(120).attr('d', () => (arcHover(d) as string));
			// show tooltip
			showTooltip(tooltipDiv, sliceTooltip(d.data), event);
		})
		.on('mousemove', (event: MouseEvent) => {
			// position tooltip with bounds-checking
			positionTooltip(tooltipDiv, event);
		})
		.on('mouseout', (event: MouseEvent, d: d3.PieArcDatum<ValueNameData>) => {
			const node = event.currentTarget as SVGPathElement;
			// if this slice isn't selected, restore size and hide tooltip
			const idx = d.index;
			if (selectedIndex !== idx) {
				d3.select(node).transition().duration(120).attr('d', () => (arc(d) as string));
				hideTooltip(tooltipDiv);
			}
			// if it's selected, keep tooltip visible (click toggles selection)
		})
		.on('click', (event: MouseEvent, d: d3.PieArcDatum<ValueNameData>) => {
			if (onValueClick) return onValueClick(d.data.nodeValue);
			const node = event.currentTarget as SVGPathElement;
			const idx = d.index;
			if (selectedIndex === idx) {
				// deselect
				selectedIndex = null;
				d3.select(node).transition().duration(120).attr('d', () => (arc(d) as string));
				hideTooltip(tooltipDiv);
			} else {
				// reset previously selected slice
				svg.selectAll<SVGPathElement, d3.PieArcDatum<ValueNameData>>('path').transition().duration(120).attr('d', (d2: d3.PieArcDatum<ValueNameData>) => (arc(d2) as string));
				selectedIndex = idx;
				// enlarge clicked slice
				d3.select(node).transition().duration(120).attr('d', () => (arcHover(d) as string));
				// show tooltip for clicked slice and position it near the click (works for touch/click)
				showTooltip(tooltipDiv, sliceTooltip(d.data), event);
			}
		});

	// Create a new arc generator to place a label close to the edge.
	// Do not show any label (name or value) for very small slices.
	// Only show labels if hide_value_labels is not enabled
	if (!opts?.hide_value_labels) {
		const texts = chartG.append('g')
			.attr('text-anchor', 'middle')
			.selectAll()
			.data(arcs)
			.join('text')
			.attr('transform', (d: d3.PieArcDatum<ValueNameData>) => {
				const c = arcLabel.centroid(d) as [number, number];
				return `translate(${c[0]},${c[1]})`;
			});

		// Hide entire text element for tiny slices
		const minAngle = opts?.min_slice_angle ?? 0.25;
		texts.filter(d => (d.endAngle - d.startAngle) <= minAngle).style('display', 'none');

		// For larger slices, add name and value
		texts.filter(d => (d.endAngle - d.startAngle) > minAngle)
			.call(text => text.append('tspan')
				.attr('y', '-0.4em')
				.attr('font-weight', '600')
				.text(d => opts?.trim_x_labels ? trimLabel(d.data.name, opts.trim_x_labels) : d.data.name)
				.each(function(this: SVGTSpanElement, d) {
					// Add SVG title for full text tooltip
					d3.select(this).append('title').text(d.data.name);
				}))
			.call(text => text.append('tspan')
				.attr('x', 0)
				.attr('y', '0.7em')
				.attr('fill-opacity', 0.7)
				.text(d => formatValue(roundDigitsAfterDecimal(d.data.value, opts?.digits_after_decimal), opts))
				.each(function(this: SVGTSpanElement, d) {
					// Add SVG title for full value tooltip
					const fullValue = formatValue(roundDigitsAfterDecimal(d.data.value, opts?.digits_after_decimal), opts);
					d3.select(this).append('title').text(fullValue);
				}));
	}

	if (showLegend) {
		const legendGap = 16;
		createLegend(svg, {
			sortedKeys,
			width,
			marginLeft: -width / 2 + mlr, // Adjust for centered coordinate system
			marginRight: width / 2 - mlr, // Adjust for centered coordinate system
			leftPosition: -width / 2 + mlr,
			marginTop: mtb,
			itemSpacing: opts?.legendItemSpacing ?? 100,
			maxItems: opts?.max_legend_items,
			legendOrientation: opts?.legend_orientation ?? 'vertical',
			values: Object.fromEntries(displayData.map(d => [d.name, formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)])),
			details: Object.fromEntries(displayData.map(d => [d.name, sliceShare(d)])),
			itemHeight: 20,
			...(sideLegend ? {
				verticalWidth: width - chartWidth - mlr - legendGap,
				position: { x: chartWidth - width / 2 + legendGap, y: 0, centerY: true },
			} : {}),
		});
	}

	// Setup global tooltip hiding with cleanup for selection state
	const cleanup = setupTooltipHiding(tooltipDiv, () => {
		selectedIndex = null;
		// restore original arc sizes
		svg.selectAll<SVGPathElement, d3.PieArcDatum<ValueNameData>>('path').attr('d', (d: d3.PieArcDatum<ValueNameData>) => (arc(d) as string));
	});

	const svgNode = svg.node();
	if (!svgNode) return null;
	
	// Store cleanup function on the SVG element
	(svgNode as ElementWithCleanup)._cleanupTooltip = cleanup;
	
	return svgNode;
}
